import * as _ from 'underscore';
import { ISiteSettingsForServerAdmin } from '../../settings/interfaces/ISite';
import { IActionContext } from '../helpers/ActionContext';
import { SiteSettingsErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { ILongRunningAction } from '../models/ILongRunningAction';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Sites } from '../resources/Sites';
import { IActionInfo } from '../ServerApi/types/IActionInfo';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ServerService } from './ServerService';
import { SiteSwitchService } from './SiteSwitchService';
import { ToasterService, ToastType } from './ToasterService';

export class DeleteSitesAction {

  public static $inject = [
    '$translate',
    '$state',
    'ServerService',
    'SiteSwitchService',
    'ConfirmActionDialog',
    'Sites',
    'ToasterService'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $state: ng.ui.IStateService,
    private server: ServerService,
    private SiteSwitchService: SiteSwitchService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private Sites: Sites,
    private ToasterService: ToasterService) { }

  private sites: Set<ISiteSettingsForServerAdmin>;
  private confirmActionDialogInstance: IConfirmActionDialogInstance;

  public execute(sites: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.ConfirmActionDialog.open({
      title: this.$translate.instant('deleteMessage'),
      bodyText: this.$translate.instant('confirmDeleteSites', {
        ITEM_COUNT: sites.size(),
        ITEM_NAME: L10n.elideIfLong(sites.first().settings.name)
      }, L10n.Interpolations.Count),
      actionName: this.$translate.instant('confirmDeleteButton', {
        ITEM_COUNT: sites.size()
      }, L10n.Interpolations.Count),
      isDeleteAction: true,
      action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => {
        this.sites = sites;
        this.confirmActionDialogInstance = confirmActionDialogInstance;
        const isAttemptingToDeleteCurrentSite = this.server.sessionSiteUrlName() !== ''
          && !_.isUndefined(this.sites.find((site) => this.server.isCurrentSiteUrlName(site.settings.urlName)));

        if (isAttemptingToDeleteCurrentSite) {
          return this.SiteSwitchService.switchIfNeededNoFeedback('')
            .then(
              () => this.deleteSites(),
              () => this.deleteSites().then(() => this.$state.go('login.authenticate'))
            );
        } else {
          return this.deleteSites();
        }
      }
    }).result;
  }

  private deleteSites(): ng.IPromise<any> {
    return this.Sites.del(this.sites.keys()).then(
      (actionResult: IActionInfo) => this.onComplete(actionResult),
      (errors: IError[]) => this.onFail(errors),
      (action: ILongRunningAction) => this.onNotify(action));
  }

  private onComplete(actionResult: IActionInfo): void {
    if (actionResult && actionResult.succeeded === false) {
      this.onFail(actionResult.response && actionResult.response.errors);
    } else {
      this.confirmActionDialogInstance.setProgress(100);
      this.ToasterService.pop({
        type: ToastType.Info,
        text: this.$translate.instant('deleteSitesSuccess', {
          ITEM_COUNT: this.sites.size()
        }, L10n.Interpolations.Count)
      });
      // Refresh session info in case we switched from multi-site to single-site
      this.server.refreshSessionInfo();
    }
  }

  private onFail(errors: IError[]): void {
    let successCount = this.sites.size();
    this.confirmActionDialogInstance.setProgress(undefined);

    if (errors && errors.length) {
      successCount -= errors.length;
      const attemptedToDeleteDefaultSiteError = _.find(errors, (e) => e.code === SiteSettingsErrorCode.AttemptedToDeleteDefaultSite);
      // Error toast if attempted to delete default site
      if (attemptedToDeleteDefaultSiteError) {
        this.ToasterService.pop({ type: ToastType.Error, text: this.$translate.instant('siteSettingsFailedAttemptedToDeleteDefaultSite') });
      }
      // Inline error message if there are errors besides attempting to delete default site
      if (errors.length > 1 || !attemptedToDeleteDefaultSiteError) {
        this.confirmActionDialogInstance.showErrorMessage(this.$translate.instant('deleteSitesFailed', {
          ITEM_COUNT: errors.length
        }, L10n.Interpolations.Count));
      }
      // If there was a partial success, display it in a success toast
      if (successCount > 0) {
        this.ToasterService.pop({
          type: ToastType.Info,
          text: this.$translate.instant('deleteSitesSuccess', {
            ITEM_COUNT: successCount
          }, L10n.Interpolations.Count)
        });
        // Refresh session info in case we switched from multi-site to single-site
        this.server.refreshSessionInfo();
      }
    } else {
      // If we have a failure with no errors, assume all delete actions failed
      this.confirmActionDialogInstance.showErrorMessage(this.$translate.instant('deleteSitesFailed', {
        ITEM_COUNT: this.sites.size()
      }, L10n.Interpolations.Count));
    }
  }

  private onNotify(action: ILongRunningAction): void {
    this.confirmActionDialogInstance.setProgress(action.percentComplete);
  }

}

Module.instance().service('DeleteSitesAction', DeleteSitesAction);
